/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {KTIcon, toAbsoluteUrl} from '../../../helpers'
import {useAuth} from '../../../../app/modules/auth'
import {notificationService} from '../../../../app/services/notification.service'
import {ChatNotification, StatusNotification} from '../../../../types/notificationTypes'
import axios from 'axios'
import {ensureBenefitDetail} from '../../../../utils/ensureBenefitDetail'

const HeaderNotificationsMenu: FC = () => {
  const navigate = useNavigate()
  const {auth, currentUser} = useAuth()
  const providerCode = currentUser?.providerCode || ''

  const [activeTab, setActiveTab] = useState<'chat' | 'status'>('chat')
  const [chatNotifications, setChatNotifications] = useState<ChatNotification[]>([])
  const [statusNotifications, setStatusNotifications] = useState<StatusNotification[]>([])
  const [chatLoading, setChatLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)

  // Fetch notifications on component mount
  useEffect(() => {
    if (!providerCode) return

    const fetchNotifications = async () => {
      // Fetch chat notifications
      setChatLoading(true)
      try {
        const chatResponse = await notificationService.getChatNotifications({
          limit: 10,
          onlyUnread: true,
        })
        if (chatResponse.isSuccess) {
          setChatNotifications(chatResponse.data.items)
        }
      } catch (error) {
        console.error('Error fetching chat notifications:', error)
      } finally {
        setChatLoading(false)
      }

      // Fetch status notifications
      setStatusLoading(true)
      try {
        const statusResponse = await notificationService.getStatusNotifications({
          limit: 10,
          onlyUnread: true,
        })
        if (statusResponse.isSuccess) {
          setStatusNotifications(statusResponse.data.items)
        }
      } catch (error) {
        console.error('Error fetching status notifications:', error)
      } finally {
        setStatusLoading(false)
      }
    }

    fetchNotifications()
  }, [providerCode])

  // Navigate to claim detail
  const handleNotificationClick = async (claimNumber: string) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/GetByClaimNumber/${claimNumber}`
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}`,
        },
      })

      if (response.data) {
        if (!ensureBenefitDetail(response.data)) return

        navigate('/inpatient', {state: {claimDetails: response.data}})
      }
    } catch (error) {
      console.error('Error fetching claim details:', error)
      navigate('/inpatient', {state: {claimNumber}})
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const chatUnreadCount = chatNotifications.filter((n) => !n.isOpen).length
  const statusUnreadCount = statusNotifications.filter((n) => !n.isOpen).length
  const totalUnreadCount = chatUnreadCount + statusUnreadCount

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-400px'
      data-kt-menu='true'
    >
      {/* Header */}
      <div
        className='d-flex flex-column bgi-no-repeat rounded-top'
        style={{backgroundImage: `url('${toAbsoluteUrl('/media/misc/menu-header-bg.jpg')}')`}}
      >
        <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
          Notifications{' '}
          <span className='fs-8 opacity-75 ps-3'>
            {totalUnreadCount > 0 ? `${totalUnreadCount} unread` : 'All read'}
          </span>
        </h3>

        {/* Tabs */}
        <ul className='nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-bold px-9'>
          <li className='nav-item'>
            <button
              className={`nav-link text-white opacity-75 opacity-state-100 pb-4 border-0 bg-transparent ${
                activeTab === 'chat' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('chat')}
            >
              Chat
              {chatUnreadCount > 0 && (
                <span className='badge badge-danger ms-2'>{chatUnreadCount}</span>
              )}
            </button>
          </li>

          <li className='nav-item'>
            <button
              className={`nav-link text-white opacity-75 opacity-state-100 pb-4 border-0 bg-transparent ${
                activeTab === 'status' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('status')}
            >
              Status
              {statusUnreadCount > 0 && (
                <span className='badge badge-warning ms-2'>{statusUnreadCount}</span>
              )}
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      <div className='tab-content'>
        {/* Chat Tab */}
        <div
          className={`tab-pane fade ${activeTab === 'chat' ? 'show active' : ''}`}
          role='tabpanel'
        >
          <div className='scroll-y mh-325px my-5 px-8'>
            {chatLoading ? (
              <div className='d-flex justify-content-center py-5'>
                <div className='spinner-border spinner-border-sm text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : chatNotifications.length === 0 ? (
              <div className='d-flex flex-column align-items-center py-10'>
                <KTIcon iconName='message-text-2' className='fs-3x text-gray-400 mb-4' />
                <p className='text-gray-500 m-0'>No chat notifications</p>
              </div>
            ) : (
              chatNotifications.map((notification, index) => (
                <div
                  key={`chat-${index}`}
                  className='d-flex flex-stack py-4 cursor-pointer'
                  onClick={() => handleNotificationClick(notification.claimNumber)}
                  style={{cursor: 'pointer'}}
                >
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-35px me-4'>
                      <span
                        className={`symbol-label ${
                          notification.isOpen ? 'bg-light-primary' : 'bg-light-danger'
                        }`}
                      >
                        <KTIcon
                          iconName='message-text-2'
                          className={`fs-2 ${notification.isOpen ? 'text-primary' : 'text-danger'}`}
                        />
                      </span>
                    </div>

                    <div className='mb-0 me-2'>
                      <span className='fs-6 text-gray-800 text-hover-primary fw-bold'>
                        {notification.claimNumber}
                      </span>
                      <div
                        className='text-gray-400 fs-7'
                        style={{
                          maxWidth: '200px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {notification.remark}
                      </div>
                    </div>
                  </div>

                  <span className='badge badge-light fs-8'>
                    {formatDate(notification.createdAt)}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className='py-3 text-center border-top'>
            <Link to='/notifications' className='btn btn-color-gray-600 btn-active-color-primary'>
              View All <KTIcon iconName='arrow-right' className='fs-5' />
            </Link>
          </div>
        </div>

        {/* Status Tab */}
        <div
          className={`tab-pane fade ${activeTab === 'status' ? 'show active' : ''}`}
          role='tabpanel'
        >
          <div className='scroll-y mh-325px my-5 px-8'>
            {statusLoading ? (
              <div className='d-flex justify-content-center py-5'>
                <div className='spinner-border spinner-border-sm text-warning' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : statusNotifications.length === 0 ? (
              <div className='d-flex flex-column align-items-center py-10'>
                <KTIcon iconName='notification-status' className='fs-3x text-gray-400 mb-4' />
                <p className='text-gray-500 m-0'>No status notifications</p>
              </div>
            ) : (
              statusNotifications.map((notification, index) => (
                <div
                  key={`status-${index}`}
                  className='d-flex flex-stack py-4 cursor-pointer'
                  onClick={() => handleNotificationClick(notification.claimNumber)}
                  style={{cursor: 'pointer'}}
                >
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-35px me-4'>
                      <span
                        className={`symbol-label ${
                          notification.isOpen ? 'bg-light-warning' : 'bg-light-danger'
                        }`}
                      >
                        <KTIcon
                          iconName='notification-status'
                          className={`fs-2 ${notification.isOpen ? 'text-warning' : 'text-danger'}`}
                        />
                      </span>
                    </div>

                    <div className='mb-0 me-2'>
                      <span className='fs-6 text-gray-800 text-hover-primary fw-bold'>
                        {notification.claimNumber}
                      </span>
                      <div className='text-gray-400 fs-7'>
                        <span className='badge badge-light-info badge-sm me-2'>
                          {notification.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <span className='badge badge-light fs-8'>
                    {formatDate(notification.createdAt)}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className='py-3 text-center border-top'>
            <Link to='/notifications' className='btn btn-color-gray-600 btn-active-color-primary'>
              View All <KTIcon iconName='arrow-right' className='fs-5' />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export {HeaderNotificationsMenu}
